import { LinearProgress, Link, makeStyles, Typography } from "@material-ui/core";
import { useQuery } from "@redux-requests/react";
import { Formik } from "formik";
// import moment from "moment";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import * as yup from "yup";
import { SelectField, SubmitButton, TextField } from "../../components/formik";
import { getCountries, signUp } from "../../ducks/actions";
import * as types from "../../ducks/types";
import { useQueryParams } from "../../hooks";
import useActionRequest from "../../hooks/useActionRequest";

const useStyles = makeStyles(theme => ({
  alignLeft: {
    alignSelf: "flex-start"
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    alignSelf: "flex-start"
  },
  fullWidth: {
    width: "100%"
  },
  submitButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3)
  }
}));

const RegisterForm = () => {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [{ accessCode }] = useQueryParams();

  const validationSchema = yup.object().shape({
    email: yup.string().email().required(),
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    title: yup.string(),
    country: yup.string().required(),
    password: yup.string().required(),
    passwordConfirmation: yup.string().required()
  });

  useEffect(() => {
    dispatch(getCountries());
  }, [dispatch]);

  // const { congress } = useCongress();

  const {
    data: { data: countries },
    loading: countriesLoading
  } = useQuery({
    type: types.GET_COUNTRIES,
    defaultData: { data: [] }
  });

  const countryOptions = countries.map(({ id, name }) => ({
    value: id,
    label: name
  }));

  const signUpRequest = useActionRequest({
    action: signUp,
    redirect: "home",
    successMessage: intl.formatMessage({
      id: "Auth.RegisterForm.SignUpSuccess",
      defaultMessage: "Registered"
    }),
    errorMessage: intl.formatMessage({
      id: "Auth.RegisterForm.SignUpError",
      defaultMessage: "Not registered"
    })
  });

  const onSubmit = user => {
    signUpRequest({ user, accessCode });
  };

  return (
    <Formik
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      initialValues={{
        email: "",
        firstName: "",
        lastName: "",
        title: "",
        country: "",
        password: "",
        passwordConfirmation: ""
      }}
    >
      <>
        <Typography variant="h4" className={classes.alignLeft} gutterBottom>
          {intl.formatMessage({ id: "Auth.RegisterForm.Register", defaultMessage: "Register" })}
        </Typography>
        <Typography variant="body1" className={classes.alignLeft}>
          {intl.formatMessage({
            id: "Auth.RegisterForm.Helper",
            defaultMessage:
              "To complete your registration use the email address where you received the invitation, fill this form and setup your password."
          })}
        </Typography>
        <TextField
          name="email"
          variant="outlined"
          label={intl.formatMessage({
            id: "Auth.RegisterForm.EmailAddress",
            defaultMessage: "E-mail address"
          })}
          fullWidth
        />
        <TextField
          name="firstName"
          variant="outlined"
          label={intl.formatMessage({
            id: "Auth.RegisterForm.FirstName",
            defaultMessage: "First Name"
          })}
          fullWidth
        />
        <TextField
          name="lastName"
          variant="outlined"
          label={intl.formatMessage({
            id: "Auth.RegisterForm.LastName",
            defaultMessage: "Last Name"
          })}
          fullWidth
        />
        <TextField
          name="title"
          variant="outlined"
          label={intl.formatMessage({
            id: "Auth.RegisterForm.Title",
            defaultMessage: "Title"
          })}
          fullWidth
        />
        {countriesLoading ? (
          <LinearProgress className={classes.fullWidth} />
        ) : (
          <SelectField
            name="country"
            variant="outlined"
            label={intl.formatMessage({
              id: "Auth.RegisterForm.Country",
              defaultMessage: "Country"
            })}
            options={countryOptions}
            elevation={8}
            fullWidth
          />
        )}
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          <strong>
            {intl.formatMessage({
              id: "imf.RegisterForm.SetupYourPassword",
              defaultMessage: "Setup your password"
            })}
          </strong>
        </Typography>
        <TextField
          name="password"
          type="password"
          variant="outlined"
          label={intl.formatMessage({
            id: "Auth.RegisterForm.Password",
            defaultMessage: "Password"
          })}
          fullWidth
        />
        <TextField
          name="passwordConfirmation"
          type="password"
          variant="outlined"
          label={intl.formatMessage({
            id: "Auth.RegisterForm.RepeatPassword",
            defaultMessage: "Repeat Password"
          })}
          fullWidth
        />
        <Typography variant="body2" gutterBottom>
          {intl.formatMessage({
            id: "Auth.RegisterForm.SignUpHelper",
            defaultMessage:
              "By clicking the “Sign Up” button, you are creating your account, and you agree to our Terms of Use and Privacy Policy."
          })}
          &nbsp;
          <Link to="/terms-of-use" component={RouterLink}>
            {intl.formatMessage({
              id: "Auth.RegisterForm.TermsOfUse",
              defaultMessage: "Terms of Use"
            })}
          </Link>
          &nbsp;
          {intl.formatMessage({
            id: "Auth.RegisterForm.And",
            defaultMessage: "and"
          })}
          &nbsp;
          <Link to="/privacy-policy" component={RouterLink}>
            {intl.formatMessage({
              id: "Auth.RegisterForm.PrivacyPolicy",
              defaultMessage: "Privacy Policy"
            })}
          </Link>
        </Typography>
        <SubmitButton
          variant="contained"
          color="primary"
          className={classes.submitButton}
          fullWidth
        >
          {intl.formatMessage({ id: "Auth.RegisterForm.SignUp", defaultMessage: "Sign up" })}
        </SubmitButton>
        <Typography>
          {intl.formatMessage({
            id: "Auth.RegisterForm.AlreayRegistered",
            defaultMessage: "Already registered?"
          })}
          &nbsp;
          <Link to="/login" component={RouterLink}>
            {intl.formatMessage({
              id: "Auth.RegisterForm.Login",
              defaultMessage: "Login"
            })}
          </Link>
        </Typography>
      </>
    </Formik>
  );
};

export default RegisterForm;
